/**
 * What a skin is (C1, C2) — and, just as importantly, what it cannot be.
 *
 * ## A skin supplies art. It never supplies positions.
 *
 * H4 decided this, and it is the reason this file is data rather than code.
 * Playfield geometry is fixed and identical under every skin: rails, felt,
 * runners, backgrounds and props are drawn *into* a layout that never moves.
 *
 * The rule is kept structurally rather than by convention. `Skin` has no field
 * that can express a length, an offset or an order, so a skin cannot move a
 * card even by accident — and `validateSkin` rejects keys outside the
 * allowlist, so a JSON manifest loaded at runtime cannot smuggle one in later
 * without somebody deliberately widening this file.
 *
 * What that buys: the 320px layout is tested once rather than once per theme.
 * Without it, every skin we sell and every skin we commission is a fresh chance
 * to break a phone, and QA becomes skins x widths forever.
 *
 * ## Faces can vary, and today they do not
 *
 * C3 decided that card faces *may* differ per skin. The engineering for that is
 * free only if the seam exists from the start — "every skin points at the same
 * face set" and "each skin points at its own" are the same code reading
 * different data. Retrofitting means touching the card layer twice, which is
 * the exact thing C1 exists to avoid. So the seam is here and exactly one face
 * set goes through it.
 */

/**
 * Where card faces come from.
 *
 * `classic` is drawn by the stylesheet from the rank and suit text - no art, no
 * download, and legible at any size because it is a font rather than a picture.
 * It is the free default and the accessibility fallback (C5): a themed deck
 * that is hard to read at 30px is a bug rather than a style, and Classic is
 * what an override falls back *to*.
 */
export type FaceSet =
  | { kind: 'classic' }
  | { kind: 'sprite'; src: string }
  /**
   * One image per card, which is what a painted deck actually arrives as.
   *
   * C4 asked for a sprite sheet and C13 corrects it: the first deck we own is
   * fifty-six `.webp` files, not one sheet. A sheet is better for a hundred
   * requests and worse for everything else - it cannot be drawn until the
   * whole thing has downloaded, and one bad card means redrawing the sheet.
   *
   * The file is named for the card id, so `base` plus the id plus `ext` is the
   * whole rule and a deck needs no manifest here. `aliases` exists for the two
   * cards where that is not true: this app calls the jokers `JR` and `JB`, and
   * a deck delivered with `JOKER1`/`JOKER2` should not have to be renamed to
   * be usable. A deck that names its files by card id needs none.
   */
  | {
      kind: 'cards';
      /** Directory the images live in, with no trailing slash. */
      base: string;
      /** Extension, with no dot. */
      ext: string;
      aliases?: Readonly<Record<string, string>>;
    };

/**
 * Where one card's picture lives, for a face set that has pictures.
 *
 * Null for Classic, which has none - the caller draws text instead. Kept here
 * beside the type rather than in the component, because "what file is this
 * card" is a fact about the face set and the card-flight layer needs the same
 * answer the hand does.
 */
export function faceImage(faces: FaceSet, card: string): string | null {
  if (faces.kind !== 'cards') return null;
  const name = faces.aliases?.[card] ?? card;
  return `${faces.base}/${name}.${faces.ext}`;
}

/**
 * A painted surface: either a CSS paint value or an image.
 *
 * Deliberately not "a colour". The card back is a repeating gradient today and
 * will be an image once C8 lands, and both are the same kind of thing to the
 * renderer - something to paint a rectangle with.
 */
export type Surface =
  | { kind: 'paint'; value: string; edge?: string }
  /**
   * `wide` is art direction, not a resolution swap.
   *
   * A table photographed for a phone is a tall oval with the rail cropped off
   * the sides; the same room photographed for a laptop is a different
   * composition, not the same one scaled. `srcset` solves the wrong problem -
   * it picks a size for one image - so the two images are named separately and
   * a media query chooses. A skin with only `src` uses it at every width.
   */
  | { kind: 'image'; src: string; wide?: string; edge?: string };

export interface Skin {
  id: string;
  name: string;
  /** C3: may vary per skin. Today every skin points at Classic. */
  faces: FaceSet;
  /** C8. The one slot with real visual payoff for almost no weight. */
  back: Surface;
  /** C9. The felt, or a velvet runner. */
  tableSurface?: Surface;
  /** C10. Full-scene art, and the only genuinely heavy slot - see C12. */
  environment?: Surface;
  /**
   * A house mark, printed on the felt.
   *
   * Optional, and most skins will not have one: a table carrying somebody
   * else's logo is that table's business, not ours. Classic has it because
   * Classic IS ours - it is the mark that says whose table this is when the
   * app row no longer carries one.
   *
   * Art, like every other slot. Where it sits and how far it lies back into
   * the table's plane are the stylesheet's, which is H4 and C-zero: a skin
   * supplies art, never positions.
   */
  mark?: Surface;
}

/**
 * Every key a skin may have.
 *
 * The allowlist exists so that adding a slot is a decision somebody makes here,
 * in the open, rather than something a manifest can do to the renderer from
 * outside. Avatars and props are NOT in it: avatars already have their own
 * system in `render/avatars.ts` and merging the two is its own decision, and
 * props are C11, still marked "discuss".
 */
const SKIN_KEYS = ['id', 'name', 'faces', 'back', 'tableSurface', 'environment', 'mark'] as const;

/**
 * Today's deck, and the floor everything else is measured against.
 *
 * The back is the gradient the stylesheet used to hardcode - in two places,
 * which is exactly the divergence C1 exists to stop. `.card.back` drew it and
 * `.card-flight` drew a copy of it, with a comment claiming the two were the
 * same. They were, by hand, until the first skin changed one of them.
 */
export const CLASSIC: Skin = {
  id: 'classic',
  name: 'Classic',
  faces: { kind: 'classic' },
  back: {
    kind: 'paint',
    value: 'repeating-linear-gradient(45deg, #1e3a8a, #1e3a8a 6px, #1d4ed8 6px, #1d4ed8 12px)',
    edge: '#1e3a8a',
  },
  /*
   * The felt (C9).
   *
   * Was hardcoded in `.room-screen`, which is the same shape of problem the
   * card back had: a skin that changed the table would have had to find it
   * there. The tokens are still the app's - a skin supplies art, and --felt is
   * a design token rather than art - so Classic's surface is written in terms
   * of them and a themed one would bring its own image instead.
   */
  tableSurface: {
    kind: 'paint',
    value:
      'radial-gradient(ellipse 130% 60% at 50% 40%, var(--felt) 0%, var(--felt-dark) 70%, var(--ink) 100%)',
  },
  /*
   * The room the table is in (C10).
   *
   * The felt above is still Classic's surface and is what shows if this ever
   * fails to load - a green table rather than a black screen. With the art
   * present it is behind it and never seen, which is the right relationship
   * between a fallback and the thing it stands in for.
   *
   * Two compositions, not two sizes: the portrait image is a tall oval with
   * the rail cropped at the sides, and the landscape one is a different
   * framing of the same room.
   */
  environment: {
    kind: 'image',
    src: '/art/table-default-tall.webp',
    wide: '/art/table-default-wide.webp',
  },
  /*
   * Ours, on our table.
   *
   * The wordmark used to sit in the app row above the table, where it cost a
   * cell of a strip that a game's name and a menu button were already
   * competing for - at 320px the name truncated to "Craz…" to make room for
   * it. Printed on the felt it is more present than it was and costs nothing,
   * which is the trade worth making for a logo.
   *
   * One image at both compositions: this is laid into the table's plane by the
   * stylesheet rather than cropped with the room, so there is no second
   * framing to art-direct.
   */
  mark: { kind: 'image', src: '/art/logo-small.webp' },
};

/**
 * Every painted theme, as the two or three facts that differ between them.
 *
 * Written as data rather than as a `Skin` each, because the skins are now
 * identical in shape: a deck of per-card images under `/art/deck/<id>`, a
 * back beside them, and a table in two compositions. Ten themes of that
 * written out by hand is nine chances to mistype a path, and the tenth theme
 * is the one where somebody copies the ninth and forgets to change the back.
 *
 * `edge` is the one thing worth choosing per theme: the back is a photograph
 * with a border painted into it, and a card edge that fights it reads as a
 * printing fault rather than a shadow.
 */
const PAINTED: ReadonlyArray<{ id: string; name: string; edge: string }> = [
  { id: 'medieval', name: 'Medieval', edge: '#d9c9a3' },
  { id: 'speakeasy', name: 'Speakeasy', edge: '#c8a24a' },
  { id: 'space-age', name: 'Space Age', edge: '#b9c6d6' },
  { id: 'wild-west', name: 'Wild West', edge: '#c8b48a' },
  { id: 'trick-or-treat', name: 'Trick or Treat', edge: '#e8952f' },
  { id: 'nevermore', name: 'Nevermore', edge: '#7a1f1f' },
  { id: 'sleigh-bells', name: 'Sleigh Bells', edge: '#1f6b3a' },
  { id: 'winters-eve', name: "Winter's Eve", edge: '#c9b06a' },
  { id: 'jolly-roger', name: 'Jolly Roger', edge: '#8a6a3a' },
  { id: 'privateer', name: 'Privateer', edge: '#c9a44a' },
];

function painted({ id, name, edge }: { id: string; name: string; edge: string }): Skin {
  return {
    id,
    name,
    /* One image per card, named for the card id - so the jokers need no
       aliases the way Medieval's did, because the slicer names them `JR` and
       `JB` on the way out. Medieval keeps its aliases below. */
    faces: { kind: 'cards', base: `/art/deck/${id}`, ext: 'webp' },
    back: { kind: 'image', src: `/art/deck/${id}/back.webp`, edge },
    /*
     * No `tableSurface`: the felt underneath is only ever seen if the
     * environment fails to load, and Classic's green is a better thing to fall
     * back to than a second painted surface that might fail the same way.
     *
     * And no `mark`. Classic carries ours because Classic IS ours; a bought
     * table is the buyer's, and printing our logo on something somebody paid
     * for is the kind of thing that makes a purchase feel rented.
     */
    environment: {
      kind: 'image',
      src: `/art/table-${id}-tall.webp`,
      wide: `/art/table-${id}-wide.webp`,
    },
  };
}

const SKINS: Record<string, Skin> = { classic: CLASSIC };
for (const theme of PAINTED) SKINS[theme.id] = painted(theme);

/*
 * Medieval was delivered with its jokers named for what they are rather than
 * for the ids this app uses, and it is the only deck that was - the slicer
 * names them by card id now. Two entries beat renaming somebody else's art.
 */
const MEDIEVAL_FACES = SKINS['medieval']?.faces;
if (MEDIEVAL_FACES && MEDIEVAL_FACES.kind === 'cards') {
  MEDIEVAL_FACES.aliases = { JR: 'JOKER1', JB: 'JOKER2' };
}

export function skinById(id: string | undefined): Skin {
  return (id !== undefined && SKINS[id]) || CLASSIC;
}

/**
 * One skin out of a deck id and a table id, which may come from two purchases.
 *
 * D87 made these separate things on purpose - the deck and the table are both
 * the room's, but they are bought and equipped apart - so the renderer has to
 * be able to hold a Medieval deck on the default felt, and will have to hold
 * combinations nobody has thought of yet.
 *
 * The same object comes back when both ids name one skin, which is the common
 * case and every case today. That is not an optimisation for its own sake:
 * `SkinProvider` memoises its CSS on skin identity, so a fresh object per
 * packet would rebuild the custom properties on every tick of every table.
 */
export function composeSkin(deckId?: string, tableId?: string): Skin {
  const deck = skinById(deckId);
  const room = skinById(tableId);
  if (deck === room) return deck;

  /*
   * Built up rather than spread from the deck.
   *
   * Spreading looks equivalent and is not: a table skin with no mark would
   * leave the DECK's mark in place, so a Medieval table under a Classic deck
   * would print our logo on a table somebody paid for. Every slot the room
   * owns is taken from the room or left out.
   */
  const composed: Skin = {
    id: `${deck.id}+${room.id}`,
    name: deck.name === room.name ? deck.name : `${deck.name} on ${room.name}`,
    faces: deck.faces,
    back: deck.back,
  };
  if (room.tableSurface) composed.tableSurface = room.tableSurface;
  if (room.environment) composed.environment = room.environment;
  if (room.mark) composed.mark = room.mark;
  return composed;
}

/**
 * The same skin with Classic's faces on it (C7).
 *
 * Faces only. A card back and a table are not a reading problem, and replacing
 * them would turn "I need to be able to read the cards" into "I cannot see
 * what everybody else is playing on" - which is a worse place to put somebody
 * who asked for help.
 *
 * The same object comes back when the faces are already Classic, so a player
 * with the setting on at a Classic table costs nothing.
 */
export function withClassicFaces(skin: Skin): Skin {
  if (skin.faces.kind === 'classic') return skin;
  return { ...skin, faces: CLASSIC.faces };
}

/**
 * What is wrong with this manifest, as a list of complaints.
 *
 * Returns an empty array for a good skin. Modelled on the toolkit's
 * `validateZoneLayout`: the useful version of a validator reports everything
 * wrong at once rather than throwing on the first problem, because a manifest
 * is usually wrong in more than one way.
 */
export function validateSkin(value: unknown): string[] {
  const problems: string[] = [];

  if (typeof value !== 'object' || value === null) {
    return ['a skin must be an object'];
  }
  const skin = value as Record<string, unknown>;

  for (const key of Object.keys(skin)) {
    if (!(SKIN_KEYS as readonly string[]).includes(key)) {
      problems.push(
        `unknown key "${key}": a skin supplies art and never positions, so the ` +
          `allowed keys are ${SKIN_KEYS.join(', ')}`,
      );
    }
  }

  if (typeof skin['id'] !== 'string' || skin['id'] === '') problems.push('id must be a non-empty string');
  if (typeof skin['name'] !== 'string' || skin['name'] === '') {
    problems.push('name must be a non-empty string');
  }

  const faces = skin['faces'];
  if (typeof faces !== 'object' || faces === null) {
    problems.push('faces is required');
  } else {
    const kind = (faces as Record<string, unknown>)['kind'];
    if (kind === 'cards') {
      const set = faces as Record<string, unknown>;
      if (typeof set['base'] !== 'string' || set['base'] === '') {
        problems.push('a per-card face set needs a base directory');
      }
      if (typeof set['ext'] !== 'string' || set['ext'] === '') {
        problems.push('a per-card face set needs a file extension');
      }
      if (set['aliases'] !== undefined && (typeof set['aliases'] !== 'object' || set['aliases'] === null)) {
        problems.push('faces.aliases must be an object of card id to file name');
      }
    } else if (kind === 'sprite') {
      if (typeof (faces as Record<string, unknown>)['src'] !== 'string') {
        problems.push('a sprite face set needs a src');
      }
      /*
       * Refused until there is something to draw it with (R5).
       *
       * `Card` has the branch and renders `<span class="card-art">` with the
       * sprite as a background image - and the stylesheet has no `.card-art`
       * rule at all, so the span has no box and the face is invisible. A skin
       * shipping this would not fail, it would deal blank cards.
       *
       * The branch and the type stay, because the sprite face set is a planned
       * item (C4) waiting on a 54-card sheet; what cannot stay is a validator
       * that accepts a skin the renderer cannot draw. Delete this check in the
       * same change that adds the CSS.
       */
      problems.push(
        'sprite face sets are not drawable yet: the renderer has no CSS for them (C4/R5)',
      );
    } else if (kind !== 'classic') {
      problems.push(
        `faces.kind must be "classic", "cards" or "sprite", not ${JSON.stringify(kind)}`,
      );
    }
  }

  for (const slot of ['back', 'tableSurface', 'environment', 'mark'] as const) {
    const surface = skin[slot];
    if (surface === undefined) {
      if (slot === 'back') problems.push('back is required: every skin has a card back');
      continue;
    }
    if (typeof surface !== 'object' || surface === null) {
      problems.push(`${slot} must be an object`);
      continue;
    }
    const s = surface as Record<string, unknown>;
    if (s['kind'] === 'paint') {
      if (typeof s['value'] !== 'string') problems.push(`${slot}.value must be a string`);
    } else if (s['kind'] === 'image') {
      if (typeof s['src'] !== 'string') problems.push(`${slot}.src must be a string`);
    } else {
      problems.push(`${slot}.kind must be "paint" or "image", not ${JSON.stringify(s['kind'])}`);
    }
  }

  return problems;
}

/** A surface as something CSS can paint with. */
function paint(surface: Surface, src?: string): string {
  if (surface.kind === 'paint') return surface.value;
  return `center / cover no-repeat url("${src ?? surface.src}")`;
}

/**
 * A skin as CSS custom properties.
 *
 * The renderer hands the whole skin to the DOM once, at the top, and every rule
 * that needs a piece of it reads a variable. That is what makes "one component
 * owns card rendering" true of the *art* as well as the markup: the flying card
 * and the card in your hand paint from the same property, so they cannot drift.
 *
 * Only art is emitted. There is deliberately no branch here that could produce
 * a width, a position or a z-index.
 */
export function skinVars(skin: Skin): Record<string, string> {
  const vars: Record<string, string> = {
    '--card-back': paint(skin.back),
    '--card-back-edge': skin.back.edge ?? 'transparent',
  };

  if (skin.tableSurface) vars['--table-surface'] = paint(skin.tableSurface);

  if (skin.environment) {
    vars['--environment'] = paint(skin.environment);
    /*
     * Emitted only when the skin has a second composition. The stylesheet's
     * landscape rule falls back to the portrait one, so a skin with a single
     * image needs no media query written for it and gets none.
     */
    if (skin.environment.kind === 'image' && skin.environment.wide !== undefined) {
      vars['--environment-wide'] = paint(skin.environment, skin.environment.wide);
    }
  }

  /*
   * No `-wide` twin. The environment has two compositions because a room
   * photographed for a phone is framed differently; a mark lying flat on the
   * felt is the same mark at every width, and the stylesheet sizes it against
   * the table it is printed on.
   */
  if (skin.mark) vars['--table-mark'] = paint(skin.mark);

  return vars;
}
